<template>
    <div>
        <div class="vertical" style="height: 80vh; width: 90vw;">
            <div class="horizontal">
                <div v-if="leftSelected.length > 0" style="margin: 5px 20px;">
                    <label>{{ $t('leftAxis') }}</label>
                    <select v-model="leftSelected" class="sensors-select">
                        <option v-for="chartType in leftTypes" :key="chartType">{{ chartType }}</option>
                    </select>
                </div>
                <div v-else style="margin: 5px 20px;">
                    {{ $t('noDataFromSensors') }}
                </div>
                <div v-if="rightSelected.length > 0" style="margin: 5px 20px;">
                    <label>{{ $t('rightAxis') }}</label>
                    <select v-model="rightSelected" class="sensors-select">
                        <option v-for="chartType in rightTypes" :key="chartType">{{ chartType }}</option>
                    </select>
                </div>
                <button class="button-reload" @click="reload">{{ $t('Reload') }}</button>
                <div style="margin: 0 10px; border-right: 2px solid #617590;"></div>
                <div>
                    <div v-if="leftSelected.length > 0" style="margin: 5px 20px;">
                        <label>{{ $t('leftMovingAvg') }}</label>
                        <select v-model="leftMovingAvg" class="sensors-select">
                            <option v-for="movingValue in movingAvgValues" :key="movingValue">{{ movingValue }}</option>
                        </select>
                    </div>
                </div>
                <div>
                    <div v-if="rightSelected.length > 0" style="margin: 5px 20px;">
                        <label>{{ $t('rightMovingAvg') }}</label>
                        <select v-model="rightMovingAvg" class="sensors-select">
                            <option v-for="movingValue in movingAvgValues" :key="movingValue">{{ movingValue }}</option>
                        </select>
                    </div>
                </div>
                <button class="button-reload" @click="reload">{{ $t('Apply') }}</button>
            </div>
            <div style="position: relative; width: 100%; height: 100%;">
                <div :id="'scichart-root-' + sid" style="width: 100%; height: 100%; margin-right: 10px; align-self: center;"></div>
                <div v-if="loading" class="chart-loading-overlay">
                    <Spinner :message="$t('Loading') + '...'"></Spinner>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import { SciChartSurface } from "scichart/Charting/Visuals/SciChartSurface";
import { NumericAxis } from "scichart/Charting/Visuals/Axis/NumericAxis";
import { EAxisAlignment } from "scichart/types/AxisAlignment";
import { FastLineRenderableSeries } from "scichart/Charting/Visuals/RenderableSeries/FastLineRenderableSeries";
import { XyDataSeries } from "scichart/Charting/Model/XyDataSeries";
import { ZoomPanModifier } from "scichart/Charting/ChartModifiers/ZoomPanModifier";
import { MouseWheelZoomModifier } from "scichart/Charting/ChartModifiers/MouseWheelZoomModifier";
import { ZoomExtentsModifier } from "scichart/Charting/ChartModifiers/ZoomExtentsModifier";
import { RolloverModifier } from "scichart/Charting/ChartModifiers/RolloverModifier";
import { chartThemeMixin } from "@/utils/chartTheme";
import { NumberRange } from "scichart/Core/NumberRange";
import { EXyDirection } from "scichart/types/XyDirection";

import moment from 'moment'
import Spinner from 'vue-simple-spinner'
import { getSensorsChartData } from '@api'

// Kolory linii lewej i prawej osi (tytuł osi ma kolor swojej linii). Granat
// i ciemna zieleń z trybu jasnego zlewały się z ciemnym tłem wykresu, więc
// w trybie ciemnym jasny błękit i jasna zieleń. Tekst popupu rollovera musi
// kontrastować z jego tłem, które ma kolor linii.
const SERIES_PALETTES = {
    light: { left: '#000088', right: '#008800', tooltipText: '#ffffff', rolloverLine: '#0095B2' },
    dark: { left: '#5EB0FF', right: '#4ADE80', tooltipText: '#0e1517', rolloverLine: '#8ea2a6' }
};

export default {
    mixins: [chartThemeMixin],
    components: { Spinner },
    props: ['systemId', 'sid'],
    data() {
        return {
            loading: true,
            sciChartSurface: null,
            wasmContext: null,

            xAxis: null,
            yAxisLeft: null,
            yAxisRight: null,
            leftLineSeries: null,
            rightLineSeries: null,
            rolloverModifier: null,

            leftSelected: '',
            rightSelected: '',
            leftMovingAvg: 1,
            rightMovingAvg: 1,

            init: false,

            charts: [],
            data: [],
            seriesCache: {},

            movingAvgValues: [1, 3, 5, 7, 11, 15, 31, 51]
        }
    },
    computed: {
        leftTypes() {
            var result = this.charts.filter(x => x != this.rightSelected)
            result.push('None')
            return result;
        },
        rightTypes() {
            var result = this.charts.filter(x => x != this.leftSelected)
            result.push('None')
            return result;
        },
        palette() {
            return this.$store.getters.resolvedTheme === 'dark' ? SERIES_PALETTES.dark : SERIES_PALETTES.light;
        }
    },
    watch: {
        // Tło i osie przemalowuje chartThemeMixin; kolory linii i tytułów osi są nasze.
        "$store.getters.resolvedTheme"() {
            this.applyPalette();
        }
    },
    methods: {
        async initSciChart() {
            const {sciChartSurface, wasmContext} = await SciChartSurface.create("scichart-root-" + this.sid);

            this.sciChartSurface = sciChartSurface;
            this.wasmContext = wasmContext;

            this.sciChartSurface.applyTheme(this.chartTheme())

            this.xAxis = new NumericAxis(this.wasmContext, {
                axisAlignment: EAxisAlignment.Bottom,
                drawMajorBands: true,
                majorGridLineStyle: {
                    color: "#9D9D9D",
                },
            });

            this.yAxisLeft = new NumericAxis(this.wasmContext, {
                axisAlignment: EAxisAlignment.Left,
                growBy: new NumberRange(0.01, 0.01),
                id: "YAxis_Left",
                axisTitle: `${this.$t(this.leftSelected)}`,
                axisTitleStyle: {
                    fontSize: '16',
                    color: this.palette.left,
                    fontWeight: 700
                }
            });

            if(this.charts.length > 1) {
                this.yAxisRight = new NumericAxis(this.wasmContext, {
                    axisAlignment: EAxisAlignment.Right,
                    growBy: new NumberRange(0.01, 0.01),
                    id: "YAxis_Right",
                    axisTitle: `${this.$t(this.rightSelected)}`,
                    axisTitleStyle: {
                        fontSize: '16',
                        color: this.palette.right,
                        fontWeight: 700
                    }
                });
            }

            this.xAxis.growBy = new NumberRange(0.01, 0.01);

            this.sciChartSurface.xAxes.add(this.xAxis)
            this.sciChartSurface.yAxes.add(this.yAxisLeft)

            if(this.charts.length > 1) {
                this.sciChartSurface.yAxes.add(this.yAxisRight)
            }

            this.xAxis.labelProvider.formatLabel = (unixTimestamp) => {
                return moment(unixTimestamp).format("MMM DD HH:mm")
            };

            this.xAxis.labelProvider.formatCursorLabel = (unixTimestamp) => {
                return moment(unixTimestamp).format("MMM DD HH:mm")
            };

            await this.populateSciChart(true)
        },
        async reloadSciChart() {
            if(this.sciChartSurface.renderableSeries != null){
                this.sciChartSurface.renderableSeries.items = [];
            }

            await this.populateSciChart(false)
        },
        async populateSciChart(init) {
            this.populateLeftAxis();
            this.populateRightAxis();

            if(init) {
                this.sciChartSurface.chartModifiers.add(new ZoomPanModifier({ xyDirection: EXyDirection.XDirection }));
                this.sciChartSurface.chartModifiers.add(new ZoomExtentsModifier({ xyDirection: EXyDirection.XDirection }));
                this.sciChartSurface.chartModifiers.add(new MouseWheelZoomModifier({ xyDirection: EXyDirection.XDirection }));

                // Pionowa linia pod kursorem i popup z wartością przy każdej linii.
                this.rolloverModifier = new RolloverModifier({
                    rolloverLineStroke: this.palette.rolloverLine,
                    rolloverLineStrokeThickness: 1,
                    tooltipDataTemplate: (seriesInfo, tooltipTitle) => [
                        tooltipTitle,
                        seriesInfo.formattedXValue,
                        this.formatTooltipValue(seriesInfo.seriesName, seriesInfo.yValue)
                    ]
                });
                this.sciChartSurface.chartModifiers.add(this.rolloverModifier);
            }

            this.sciChartSurface.zoomExtents();
        },
        populateLeftAxis() {
            this.leftLineSeries = null;

            if(this.leftSelected == 'None') {
                this.yAxisLeft.axisTitle = '';
                return;
            }

            var series = this.seriesCache[this.leftSelected];
            if (!series) {
                return;
            }

            const dataSeries = new XyDataSeries(this.wasmContext, {
                xValues: series.x,
                yValues: this.movAvg(series.y, this.leftMovingAvg),
                dataSeriesName: this.leftSelected
            })

            this.leftLineSeries = new FastLineRenderableSeries(this.wasmContext, {
                dataSeries,
                strokeThickness: 3,
                yAxisId: "YAxis_Left"
            })
            this.leftLineSeries.rolloverModifierProps.tooltipTitle = this.$t(this.leftSelected);
            this.paintSeries(this.leftLineSeries, this.palette.left);

            this.applyAxisLabelFormatter(this.yAxisLeft, this.leftSelected);
            this.yAxisLeft.axisTitle = this.$t(this.leftSelected);
            this.sciChartSurface.renderableSeries.add(this.leftLineSeries);
        },
        populateRightAxis() {
            this.rightLineSeries = null;

            if(this.rightSelected == 'None') {
                this.yAxisRight.axisTitle = '';
                return;
            }

            var series = this.seriesCache[this.rightSelected];
            if (!series) {
                return;
            }

            const dataSeries = new XyDataSeries(this.wasmContext, {
                xValues: series.x,
                yValues: this.movAvg(series.y, this.rightMovingAvg),
                dataSeriesName: this.rightSelected
            })

            this.rightLineSeries = new FastLineRenderableSeries(this.wasmContext, {
                dataSeries,
                strokeThickness: 2,
                yAxisId: "YAxis_Right"
            })
            this.rightLineSeries.rolloverModifierProps.tooltipTitle = this.$t(this.rightSelected);
            this.paintSeries(this.rightLineSeries, this.palette.right);

            this.applyAxisLabelFormatter(this.yAxisRight, this.rightSelected);
            this.yAxisRight.axisTitle = this.$t(this.rightSelected);
            this.sciChartSurface.renderableSeries.add(this.rightLineSeries);
        },
        paintSeries(lineSeries, color) {
            lineSeries.stroke = color;
            lineSeries.rolloverModifierProps.tooltipColor = color;
            lineSeries.rolloverModifierProps.markerColor = color;
            lineSeries.rolloverModifierProps.tooltipTextColor = this.palette.tooltipText;
        },
        applyPalette() {
            if (!this.sciChartSurface) {
                return;
            }

            const palette = this.palette;
            if (this.leftLineSeries) {
                this.paintSeries(this.leftLineSeries, palette.left);
            }
            if (this.rightLineSeries) {
                this.paintSeries(this.rightLineSeries, palette.right);
            }
            if (this.yAxisLeft) {
                this.yAxisLeft.axisTitleStyle = { color: palette.left };
            }
            if (this.yAxisRight) {
                this.yAxisRight.axisTitleStyle = { color: palette.right };
            }
            if (this.rolloverModifier) {
                this.rolloverModifier.rolloverLineStroke = palette.rolloverLine;
            }
        },
        reload() {
            this.sciChartSurface.renderableSeries.items = [];
            this.populateLeftAxis();
            this.populateRightAxis();

            this.sciChartSurface.zoomExtents();
        },
        applyAxisLabelFormatter(axis, selected) {
            if (!axis) {
                return;
            }

            // Uptime jest w sekundach - pokazujemy czytelny czas (np. "23d 4h") zamiast surowej liczby.
            if (selected === 'Uptime') {
                axis.labelProvider.formatLabel = (dataValue) => this.formatUptime(dataValue);
                axis.labelProvider.formatCursorLabel = (dataValue) => this.formatUptime(dataValue);
            } else {
                axis.labelProvider.formatLabel = (dataValue) => parseInt(dataValue);
                axis.labelProvider.formatCursorLabel = (dataValue) => parseInt(dataValue);
            }
        },
        // Oś pokazuje liczby całkowite, popup — wartość z dwoma miejscami po przecinku.
        formatTooltipValue(selected, value) {
            if (selected === 'Uptime') {
                return this.formatUptime(value);
            }
            return String(Math.round(value * 100) / 100);
        },
        formatUptime(seconds) {
            seconds = Math.floor(seconds);
            if (!isFinite(seconds) || seconds < 0) {
                return '';
            }

            const d = Math.floor(seconds / 86400);
            const h = Math.floor((seconds % 86400) / 3600);
            const m = Math.floor((seconds % 3600) / 60);

            if (d > 0) {
                return `${d}d ${h}h`;
            }
            if (h > 0) {
                return `${h}h ${m}m`;
            }
            return `${m}m`;
        },
        movAvg(list, frame) {
            if (frame == 1) {
                return list;
            }

            // Prefix-sum: liczy te same okna co poprzednio, ale w O(n) zamiast O(n*frame).
            const n = list.length;
            const half = Math.floor(frame / 2);
            const prefix = new Float64Array(n + 1);
            for (let i = 0; i < n; i++) {
                prefix[i + 1] = prefix[i] + list[i];
            }

            const result = new Array(n);
            for (let i = 0; i < n; i++) {
                const start = i - half < 0 ? 0 : i - half;
                const end = i + half > n - 1 ? n - 1 : i + half;
                result[i] = (prefix[end] - prefix[start]) / (end - start);
            }
            return result;
        },
        async loadNewDataset(){
            this.data = await getSensorsChartData(this.systemId)
            this.charts = this.data.map(x => x.label);

            // Backend zwraca dane juz posortowane - parsujemy timestampy raz i cache'ujemy,
            // zamiast sortowac i mapowac przy kazdym renderze osi.
            this.seriesCache = {};
            for (const series of this.data) {
                this.seriesCache[series.label] = {
                    x: series.data.map(d => new Date(d.timestamp).getTime()),
                    y: series.data.map(d => d.value)
                };
            }

            if (this.charts.length > 0) {
                this.leftSelected = this.charts[0]
            }

            if (this.charts.length > 1) {
                this.rightSelected = this.charts[1]
            }
        },
    },
    mounted: async function() {
        this.loading = true;
        try {
            await this.loadNewDataset();
            await this.initSciChart();
            this.init = true;
        } finally {
            this.loading = false;
        }
    },
    beforeDestroy: async function() {
        if(this.sciChartSurface || this.wasmContext){
            this.sciChartSurface = null
            this.wasmContext = null
        }
        this.sciChartSurface = null
        this.wasmContext = null
    },
}
</script>

<style scoped>
.sensors-select {
    padding: 3px 5px;
    width: 150px;
    border: 1px solid rgba(224, 224, 224, 1);
	border-radius: 5px;
}

.button-reload {
    color: black;
    border: 2px solid #617590;
	border-radius: 5px;
    box-sizing: border-box;
    padding: 0 10px;
    margin: 5px;
    transition-duration: 0.3s;
}

.button-reload:hover {
    color: white;
    background-color: #617590;
    border: 2px solid rgba(224, 224, 224, 1);
}

.chart-loading-overlay {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
    background: rgba(255, 255, 255, 0.7);
    z-index: 10;
}

.moving-avg-input {
    border: 2px solid #617590;
    border-radius: 5px;
    box-sizing: border-box;
    padding: 0 10px;
    margin: 5px;
}
</style>



// WEBPACK FOOTER //
// src/components/Charts/SciChartSensorsData.vue